import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient, axiosClient, useAuth, PermissionGate } from '../../auth-lib';
// Default shell is the employee area's (orgadmin variant) — this page is the
// click-through target of the employee Home/My Tasks/Compliance lists. A
// different area can still pass its own via the Layout prop.
import EmployeeLayout from '../components/EmployeeLayout';
import TaskCompletionFlow from '../../org-admin/components/TaskCompletionFlow';
// The same two components the org-admin task dashboard renders — not copies.
// The checklist is the step list for the work and the to-dos are its working
// breakdown, so the person DOING the task is the last person who should be
// unable to see them. Neither endpoint was ever gated; only this page was.
import TaskChecklistCard from '../../org-admin/components/TaskChecklistCard';
import TodoList from '../../org-admin/components/TodoList';
import { Button, Card, Input, Select, StatusPill, Skeleton, useToast } from '../../design-system';
import { formatServiceName } from '../../lib/serviceCatalog';
import '../styles/TaskDetail.css';

const MANAGER_LEVEL = 3;

const emptyAssignmentEdit = {
  preparerId: '',
  preparerHours: '',
  reviewerId: '',
  reviewerHours: '',
  assistantIds: [],
  assistantHours: {},
};

const emptyLogForm = { employeeId: '', hours: '', note: '' };

function TaskDetail({ Layout = EmployeeLayout }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editData, setEditData] = useState({});
  const [orgUsers, setOrgUsers] = useState([]);
  const [assignmentEdit, setAssignmentEdit] = useState(emptyAssignmentEdit);
  const [logForm, setLogForm] = useState(emptyLogForm);
  const [loggingTime, setLoggingTime] = useState(false);
  const [completing, setCompleting] = useState(false);
  const canLogForOthers = (user?.role?.level || 0) >= MANAGER_LEVEL;
  const myId = user?._id || user?.id;
  const toast = useToast();

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
  }, []);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/tasks/${id}`);
      setTask(response.data);
      setEditData(response.data);
    } catch (err) {
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  // Send ONLY the field being changed. Posting the whole task back would echo
  // every populated ref as an object and, on a completed task, trip the
  // "nothing but a reopen may change" guard — making it impossible to reopen
  // from here. It would also clobber anyone else's concurrent edits with a
  // snapshot taken when this page loaded.
  const handleStatusChange = async (newStatus) => {
    // ⚠️ COMPLETION GOES THROUGH ITS OWN FLOW, not this PATCH. Closing a task
    // files the compliance record, releases whatever was blocked on it and
    // drafts the invoice for its charges — all of which succeed or roll back
    // together (POST /api/tasks/:id/complete). Patching `status` from here
    // would close the task and quietly skip every one of them.
    if (newStatus === 'completed') {
      setCompleting(true);
      return;
    }
    try {
      const response = await apiClient.patch(`/api/tasks/${id}`, { status: newStatus });
      setTask(response.data);
      setEditData(response.data);
      toast.success(newStatus === 'todo' && task.status === 'completed' ? 'Task reopened' : 'Status updated');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      const response = await apiClient.post(`/api/tasks/${id}/comments`, { text: comment });
      // Merge only the comments: anything else on this page may have been
      // edited since load, and the thread is the only thing that changed.
      setTask((prev) => ({ ...prev, comments: response.data.comments }));
      setComment('');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const buildAssignmentEdit = (t) => {
    const allocations = t.hourAllocations || [];
    const findHours = (role, userId) => {
      const entry = allocations.find((a) => a.role === role && (a.userId?._id || a.userId) === userId);
      return entry ? String(entry.allocatedHours) : '';
    };
    const preparerId = t.preparerId?._id || t.preparerId || '';
    const reviewerId = t.reviewerId?._id || t.reviewerId || '';
    const assistantIds = (t.assistantIds || []).map((a) => a?._id || a);
    const assistantHours = {};
    assistantIds.forEach((userId) => {
      const hours = findHours('assistant', userId);
      if (hours) assistantHours[userId] = hours;
    });

    return {
      preparerId,
      preparerHours: preparerId ? findHours('preparer', preparerId) : '',
      reviewerId,
      reviewerHours: reviewerId ? findHours('reviewer', reviewerId) : '',
      assistantIds,
      assistantHours,
    };
  };

  const startEditing = () => {
    setAssignmentEdit(buildAssignmentEdit(task));
    setIsEditing(true);
  };

  const toggleAssistant = (userId) => {
    setAssignmentEdit((prev) => ({
      ...prev,
      assistantIds: prev.assistantIds.includes(userId)
        ? prev.assistantIds.filter((existingId) => existingId !== userId)
        : [...prev.assistantIds, userId],
    }));
  };

  const setAssistantHours = (userId, hours) => {
    setAssignmentEdit((prev) => ({
      ...prev,
      assistantHours: { ...prev.assistantHours, [userId]: hours },
    }));
  };

  const handleSaveEdits = async () => {
    const hourAllocations = [];
    if (assignmentEdit.preparerId && assignmentEdit.preparerHours) {
      hourAllocations.push({
        userId: assignmentEdit.preparerId,
        role: 'preparer',
        allocatedHours: Number(assignmentEdit.preparerHours),
      });
    }
    if (assignmentEdit.reviewerId && assignmentEdit.reviewerHours) {
      hourAllocations.push({
        userId: assignmentEdit.reviewerId,
        role: 'reviewer',
        allocatedHours: Number(assignmentEdit.reviewerHours),
      });
    }
    assignmentEdit.assistantIds.forEach((userId) => {
      const hours = assignmentEdit.assistantHours[userId];
      if (hours) hourAllocations.push({ userId, role: 'assistant', allocatedHours: Number(hours) });
    });

    const payload = {
      title: editData.title,
      description: editData.description,
      priority: editData.priority,
      dueDate: editData.dueDate,
      preparerId: assignmentEdit.preparerId || null,
      reviewerId: assignmentEdit.reviewerId || null,
      assistantIds: assignmentEdit.assistantIds,
      hourAllocations,
      // Billing is deliberately NOT sent from here. It now lives in billItems[],
      // edited through the task dashboard's Billing card. Sending the old
      // isBillable flag would be read as "this task isn't billable after all"
      // and would drop every bill on the task.
    };

    try {
      const response = await apiClient.put(`/api/tasks/${id}`, payload);
      setTask(response.data);
      setEditData(response.data);
      setIsEditing(false);
      // Reassigning a role drops the outgoing person's budgeted hours — they
      // were budget against somebody no longer on the task. Said out loud,
      // because an estimate disappearing silently is how people stop trusting
      // the hours at all.
      const dropped = response.data.droppedAllocations || [];
      if (dropped.length) {
        toast.info(`Task updated. ${dropped.length} hour allocation(s) removed — the people they were budgeted to are no longer in that role.`);
      } else {
        toast.success('Task updated');
      }
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update task');
    }
  };

  const handleRestore = async () => {
    try {
      const response = await apiClient.patch(`/api/tasks/${id}/restore`);
      setTask(response.data);
      setEditData(response.data);
      toast.success('Task restored');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore task');
    }
  };

  // Admin-side stopgap: lets whoever's managing the task log hours on
  // behalf of the preparer/reviewer/assistants, since they aren't logging
  // their own time yet. Self-logging (once that exists) will reuse the
  // same endpoint without an employeeId override.
  // Managers pick who logged time; everyone else logs against themselves.
  const handleAddTimeLog = async (e) => {
    e.preventDefault();
    const targetId = canLogForOthers ? logForm.employeeId : myId;
    if (!targetId || !logForm.hours) {
      toast.danger(canLogForOthers ? 'Pick a person and enter hours' : 'Enter hours');
      return;
    }

    setLoggingTime(true);
    try {
      const response = await apiClient.post(`/api/tasks/${id}/timelogs`, {
        employeeId: targetId,
        minutes: Math.round(Number(logForm.hours) * 60),
        note: logForm.note || undefined,
      });
      setTask(response.data);
      setLogForm(emptyLogForm);
      toast.success('Time logged');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to log time');
    } finally {
      setLoggingTime(false);
    }
  };

  // Remove a time-log entry (your own, or any if you're a manager).
  const handleRemoveTimeLog = async (logId) => {
    if (!window.confirm('Remove this time entry?')) return;
    try {
      const response = await apiClient.delete(`/api/tasks/${id}/timelogs/${logId}`);
      setTask(response.data);
      toast.success('Time entry removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove time entry');
    }
  };

  // A task carries its client on `clientId` (single) or in `clientIds[]` (bulk
  // and compliance-generated ones). Same fallback the org-admin dashboard uses —
  // reading only the first would leave the field blank on half the tasks.
  const clientList = task?.clientId ? [task.clientId] : (task?.clientIds || []);
  const taskClient = clientList[0] || null;
  const extraClients = clientList.length - 1;

  if (loading) {
    return (
      <Layout title="Task Details" subtitle="">
        <Card><Skeleton.Text lines={4} /></Card>
      </Layout>
    );
  }

  if (error || !task) {
    return (
      <Layout title="Task Details" subtitle="">
        <div className="form-error" role="alert">{error || 'Task not found'}</div>
      </Layout>
    );
  }

  const hourTracking = (task.hourAllocations || []).map((allocation) => {
    const userId = allocation.userId?._id || allocation.userId;
    const actualMinutes = (task.timeLogs || [])
      .filter((log) => (log.employeeId?._id || log.employeeId) === userId)
      .reduce((sum, log) => sum + (log.minutes || 0), 0);
    const actualHours = actualMinutes / 60;
    const variance = actualHours - allocation.allocatedHours;
    let varianceStatus = 'success';
    if (allocation.allocatedHours > 0) {
      if (variance > 0) varianceStatus = 'danger';
      else if (actualHours / allocation.allocatedHours >= 0.9) varianceStatus = 'warning';
    }
    return {
      key: `${userId}-${allocation.role}`,
      name: allocation.userId?.name || 'Unknown',
      role: allocation.role,
      allocatedHours: allocation.allocatedHours,
      actualHours,
      variance,
      varianceStatus,
    };
  });

  // Only the people actually assigned to this task can be logged against
  // (matches the backend's own validation).
  const loggablePeople = [
    task.preparerId?.name && { id: task.preparerId._id, name: task.preparerId.name, role: 'Preparer' },
    task.reviewerId?.name && { id: task.reviewerId._id, name: task.reviewerId.name, role: 'Reviewer' },
    ...(task.assistantIds || [])
      .filter((a) => a?.name)
      .map((a) => ({ id: a._id, name: a.name, role: 'Assistant' })),
  ].filter(Boolean);

  // A regular employee assigned to this task may log (and remove) their OWN
  // time. Managers keep the full "log on behalf of anyone" form.
  const isAssignedToTask = loggablePeople.some((p) => p.id === myId);
  const canLogTime = canLogForOthers || isAssignedToTask;

  const timeLogEntries = (task.timeLogs || [])
    .map((log) => ({
      id: log._id,
      name: log.employeeId?.name || 'Unknown',
      personId: log.employeeId?._id || log.employeeId,
      minutes: log.minutes || 0,
      note: log.note,
      loggedAt: log.loggedAt,
    }))
    .sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt));
  const canRemoveLog = (entry) => canLogForOthers || entry.personId === myId;

  return (
    <Layout title="Task Details" subtitle="View and manage task information">
      <div className="task-detail-page">
        {task.isDeleted && (
          <div className="deleted-task-banner">
            <span>
              🗑️ This task was deleted{task.deletedAt ? ` on ${new Date(task.deletedAt).toLocaleString()}` : ''}.
              It's hidden from every normal view but not gone — restore it to bring it back.
            </span>
            <Button variant="primary" size="sm" onClick={handleRestore}>Restore Task</Button>
          </div>
        )}

        <div className="task-header-section">
          <div className="task-title-block">
            {task.taskNumber != null && (
              <span className="task-number-badge">TASK-{String(task.taskNumber).padStart(4, '0')}</span>
            )}
            {isEditing ? (
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                className="ds-input task-title-edit"
              />
            ) : (
              <h2 className="task-detail-title">{task.title}</h2>
            )}

            {/* ⚠️ WHO THIS WORK IS FOR. This page showed no client at all, which
                made "file GSTR-3B, due the 20th" an instruction nobody could act
                on without going back to the list. The API has always returned it
                populated; only this page never rendered it.

                Plain text, NOT a link to the client — an employee holds no
                `nav.clients` grant by default, so a link would mostly lead to a
                403. Naming the client needs no such grant. */}
            <div className="task-meta-strip">
              <span className="task-meta-item">
                <span className="task-meta-label">Client</span>
                <span className="task-meta-value">
                  {taskClient ? taskClient.displayName : 'No client'}
                  {extraClients > 0 && <span className="task-meta-more"> +{extraClients} more</span>}
                </span>
              </span>
              {task.serviceId?.name && (
                <span className="task-meta-item">
                  <span className="task-meta-label">Service</span>
                  <span className="task-meta-value">{formatServiceName(task.serviceId.name)}</span>
                </span>
              )}
              {task.period && (
                <span className="task-meta-item">
                  <span className="task-meta-label">Period</span>
                  <span className="task-meta-value">{task.period}</span>
                </span>
              )}
            </div>
          </div>

          {/* A completed task is locked: the only move out is a reopen, which
              the server sends back to Pending whatever is asked for. Offering
              the full list here would let someone pick "In Progress" and
              silently land on Pending instead. */}
          <Select
            value={task.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="status-select"
          >
            {task.status === 'completed' ? (
              <>
                <option value="completed">Completed</option>
                <option value="todo">Reopen task</option>
              </>
            ) : (
              <>
                <option value="todo">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="review">Review</option>
                <option value="waiting-client">Waiting on Client</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </>
            )}
          </Select>
        </div>

        <div className="task-grid">
          <div className="task-main">
            <Card>
              <Card.Header title="Description" />
              {isEditing ? (
                <textarea
                  className="ds-input"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows="5"
                />
              ) : (
                <p className="task-description-text">{task.description || 'No description'}</p>
              )}
            </Card>

            {/* The work itself, above the conversation about it. A completed
                task is frozen server-side (the checklist and to-do routes both
                refuse writes on one), so it goes read-only here rather than
                offering controls that would 400. */}
            <TaskChecklistCard
              task={task}
              setTask={setTask}
              taskId={id}
              readOnly={task.status === 'completed' || task.isDeleted}
            />

            <Card>
              <Card.Header
                title="To-Do"
                subtitle="Smaller pieces of this task — yours and anyone else's on it"
              />
              <TodoList taskId={id} taskLinkBase="/tasks" enableDetailModal />
            </Card>

            <Card>
              <Card.Header title={`Comments (${task.comments?.length || 0})`} />
              <div className="comments-list">
                {task.comments?.map((c, idx) => (
                  <div key={idx} className="comment">
                    <div className="comment-header">
                      <strong>{c.author}</strong>
                      <small>{new Date(c.createdAt).toLocaleString()}</small>
                    </div>
                    <p>{c.text}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddComment} className="add-comment-form">
                <textarea
                  className="ds-input"
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                />
                <Button type="submit" variant="secondary" size="sm">Post Comment</Button>
              </form>
            </Card>

            {(hourTracking.length > 0 || canLogTime || timeLogEntries.length > 0) && (
              <Card>
                <Card.Header title="Hour Tracking" />
                <div className="hour-tracking-list">
                  {hourTracking.map((entry) => (
                    <div key={entry.key} className="hour-tracking-row">
                      <div className="hour-tracking-person">
                        <span className="hour-tracking-name">{entry.name}</span>
                        <span className="hour-tracking-role">{entry.role}</span>
                      </div>
                      <div className="hour-tracking-hours">
                        <span>{entry.actualHours.toFixed(2)}h logged / {entry.allocatedHours}h allocated</span>
                        <StatusPill
                          variant={entry.varianceStatus}
                          label={
                            entry.variance > 0
                              ? `+${entry.variance.toFixed(2)}h over`
                              : `${Math.abs(entry.variance).toFixed(2)}h under`
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {canLogTime && (
                  <form onSubmit={handleAddTimeLog} className="log-time-form">
                    {/* Managers choose who logged the time; an employee logs their own. */}
                    {canLogForOthers && (
                      <Select
                        value={logForm.employeeId}
                        onChange={(e) => setLogForm({ ...logForm, employeeId: e.target.value })}
                        disabled={loggingTime}
                      >
                        <option value="">-- Who logged time? --</option>
                        {loggablePeople.map((p) => (
                          <option key={p.id} value={p.id}>{p.name} ({p.role})</option>
                        ))}
                      </Select>
                    )}
                    <Input
                      type="number"
                      min="0"
                      step="0.25"
                      placeholder="Hours"
                      className="log-time-hours"
                      value={logForm.hours}
                      onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })}
                      disabled={loggingTime}
                    />
                    <Input
                      type="text"
                      placeholder="Note (optional)"
                      className="log-time-note"
                      value={logForm.note}
                      onChange={(e) => setLogForm({ ...logForm, note: e.target.value })}
                      disabled={loggingTime}
                    />
                    <Button type="submit" variant="secondary" size="sm" loading={loggingTime}>
                      Log Time
                    </Button>
                  </form>
                )}

                {timeLogEntries.length > 0 && (
                  <div className="time-log-entries">
                    <div className="time-log-entries-head">Logged entries</div>
                    {timeLogEntries.map((entry) => (
                      <div key={entry.id} className="time-log-entry">
                        <div className="time-log-entry-main">
                          <span className="time-log-entry-hours">{(entry.minutes / 60).toFixed(2)}h</span>
                          <span className="time-log-entry-name">{entry.name}</span>
                          {entry.note && <span className="time-log-entry-note">— {entry.note}</span>}
                        </div>
                        <div className="time-log-entry-meta">
                          <span>{entry.loggedAt ? new Date(entry.loggedAt).toLocaleDateString() : ''}</span>
                          {canRemoveLog(entry) && (
                            <button
                              type="button"
                              className="employee-action-link danger"
                              onClick={() => handleRemoveTimeLog(entry.id)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>

          <div className="task-sidebar">
            <Card>
              <div className="sidebar-field">
                <span className="sidebar-label">Priority</span>
                {isEditing ? (
                  <Select
                    value={editData.priority}
                    onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Select>
                ) : (
                  <StatusPill status={task.priority} />
                )}
              </div>

              <div className="sidebar-field">
                <span className="sidebar-label">Due Date</span>
                {isEditing ? (
                  <input
                    type="date"
                    className="ds-input"
                    value={editData.dueDate?.split('T')[0] || ''}
                    onChange={(e) => setEditData({ ...editData, dueDate: e.target.value })}
                  />
                ) : (
                  <span className="sidebar-value">
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'Not set'}
                  </span>
                )}
              </div>

              {/* The old "Assigned To" row is gone: it edited a separate owner
                  field that the compliance engine never wrote and the approval
                  workflow never read. Preparer is the owner. */}
              <div className="sidebar-field">
                <span className="sidebar-label">Preparer</span>
                {isEditing ? (
                  <div className="assignee-row">
                    <Select
                      value={assignmentEdit.preparerId}
                      onChange={(e) => setAssignmentEdit({ ...assignmentEdit, preparerId: e.target.value })}
                    >
                      <option value="">-- None --</option>
                      {orgUsers.map((u) => (
                        <option key={u._id} value={u._id}>{u.name}</option>
                      ))}
                    </Select>
                    {assignmentEdit.preparerId && (
                      <input
                        type="number"
                        className="ds-input assistant-hours-input"
                        min="0"
                        step="0.5"
                        placeholder="Hours"
                        value={assignmentEdit.preparerHours}
                        onChange={(e) => setAssignmentEdit({ ...assignmentEdit, preparerHours: e.target.value })}
                      />
                    )}
                  </div>
                ) : (
                  <span className="sidebar-value">{task.preparerId?.name || 'Not set'}</span>
                )}
              </div>

              <div className="sidebar-field">
                <span className="sidebar-label">Reviewer</span>
                {isEditing ? (
                  <div className="assignee-row">
                    <Select
                      value={assignmentEdit.reviewerId}
                      onChange={(e) => setAssignmentEdit({ ...assignmentEdit, reviewerId: e.target.value })}
                    >
                      <option value="">-- None --</option>
                      {orgUsers.map((u) => (
                        <option key={u._id} value={u._id}>{u.name}</option>
                      ))}
                    </Select>
                    {assignmentEdit.reviewerId && (
                      <input
                        type="number"
                        className="ds-input assistant-hours-input"
                        min="0"
                        step="0.5"
                        placeholder="Hours"
                        value={assignmentEdit.reviewerHours}
                        onChange={(e) => setAssignmentEdit({ ...assignmentEdit, reviewerHours: e.target.value })}
                      />
                    )}
                  </div>
                ) : (
                  <span className="sidebar-value">{task.reviewerId?.name || 'Not set'}</span>
                )}
              </div>

              <div className="sidebar-field">
                <span className="sidebar-label">Assistants</span>
                {isEditing ? (
                  <div className="assistants-checkbox-list">
                    {orgUsers.map((u) => {
                      const checked = assignmentEdit.assistantIds.includes(u._id);
                      return (
                        <div key={u._id} className="assistant-row">
                          <label className="assistant-checkbox">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAssistant(u._id)}
                            />
                            {u.name}
                          </label>
                          {checked && (
                            <input
                              type="number"
                              className="ds-input assistant-hours-input"
                              min="0"
                              step="0.5"
                              placeholder="Hours"
                              value={assignmentEdit.assistantHours[u._id] || ''}
                              onChange={(e) => setAssistantHours(u._id, e.target.value)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="sidebar-value">
                    {(task.assistantIds || []).map((a) => a.name).join(', ') || 'None'}
                  </span>
                )}
              </div>

              {/* Read-only here. A task can carry several bills (a professional
                  fee plus out-of-pocket expenses), which the old single
                  head+amount pair couldn't express — they're added and edited on
                  the task dashboard's Billing card, the one write path. */}
              <div className="sidebar-field">
                <span className="sidebar-label">Billing</span>
                {task.billItems?.length ? (
                  <div className="billing-lines">
                    {task.billItems.map((b) => (
                      <span key={b._id} className="sidebar-value billing-line">
                        {b.billType === 'ope' ? 'OPE' : 'Professional Fees'}
                        {b.description ? ` · ${b.description}` : ''}
                        {` — ₹${(b.amount || 0).toLocaleString('en-IN')}`}
                      </span>
                    ))}
                    {task.billItems.length > 1 && (
                      <span className="sidebar-value billing-line-total">
                        Total — ₹{(task.billAmount || 0).toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="sidebar-value">Not billable</span>
                )}
              </div>

              <div className="sidebar-field">
                <span className="sidebar-label">Created By</span>
                <span className="sidebar-value">{task.createdByRole}</span>
              </div>

              <div className="sidebar-field">
                <span className="sidebar-label">Dates</span>
                <span className="sidebar-value-sm">Created: {new Date(task.createdAt).toLocaleString()}</span>
                <span className="sidebar-value-sm">Updated: {new Date(task.updatedAt).toLocaleString()}</span>
              </div>

              <PermissionGate requiredPermission="tasks.update">
                <div className="edit-actions">
                  {isEditing ? (
                    <>
                      <Button variant="primary" size="sm" onClick={handleSaveEdits}>Save</Button>
                      <Button variant="secondary" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                    </>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={startEditing}>Edit</Button>
                  )}
                </div>
              </PermissionGate>
            </Card>
          </div>
        </div>
      </div>

      {/* Re-fetched rather than patched from the response: completion also moves
          the compliance instance and the billing state, and reconciling those
          here would duplicate rules the server already applied. */}
      {completing && (
        <TaskCompletionFlow
          task={task}
          onClose={() => setCompleting(false)}
          onDone={() => fetchTask()}
        />
      )}
    </Layout>
  );
}

export default TaskDetail;
